import {
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import vendorUser from "../../assets/images/vendorLogo.svg";
import { lockIcon, searchIcon, userIcon } from "../../assets/uppoIcons/icons";
import { AuthContext } from "../../components/auth/context/Context";
import ReactPagination from "../../components/pagination/Pagination";
import { ThemeConfig } from "../../dataTypes/ThemeConfig";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";

interface Vendor {
  id: number;
  name: string;
  path: string;
  benefits: number;
  users: number;
}

interface HomeContentProps {
  themeConfig: ThemeConfig;
}

export const Home: React.FC<HomeContentProps> = ({ themeConfig }) => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { hasRole } = useContext(AuthContext);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [searchVendor, setSearchVendor] = useState<string>("");

  /** Sets a default image if the original image fails to load.  */
  const handleImgError = (e: any) => {
    e.target.src = vendorUser;
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchVendor(event.target.value);
    setPage(0);
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor.name.toLowerCase().includes(searchVendor.toLowerCase())
  );

  const paginatedVendors = filteredVendors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No token found");
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/${slug}/admin/getVendors`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch vendors");
        }
        const data = await response.json();
        setVendors(data.data);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, [slug]);

  if (loading) {
    return (
      <Box
        minHeight="80vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        margin: "40px auto ",
        display: "flex",
        flexDirection: "column",
        maxHeight: 1000,
        width: 918,
        justifyContent: "space-between",
      }}
    >
      <Box
        maxHeight="800px"
        overflow="auto"
        width={918}
        padding="40px 130px"
        sx={{
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "11px",
              alignItems: "center",
              paddingBottom: "22px",
            }}
          >
            <TextField
              InputProps={{
                type: "search",
                sx: {},
              }}
              placeholder="Busca"
              value={searchVendor}
              onChange={handleSearchChange}
              sx={{
                marginBottom: "22px",
                width: "378px",
                margin: "0px",
                display: "flex",
                "& .MuiOutlinedInput-input ": {
                  padding: "5px 16px",
                },
              }}
            />
            {searchIcon}
          </Box>

          <Box>
            {filteredVendors.length === 0 ? (
              <Typography sx={{ textAlign: "center" }}>
                No data found
              </Typography>
            ) : (
              paginatedVendors.map((vendor: Vendor) => (
                <Box
                  key={vendor.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderTop: "1px solid #E0E0E0",
                    height: "210px",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ margin: "12px 21px 12px 7px " }}>
                    <img
                      src={vendor.path}
                      style={{
                        marginBottom: "0px",
                        maxWidth: "170px",
                        objectFit: "fill",
                      }}
                      onError={handleImgError}
                      alt={vendor.name}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "500",
                          marginBottom: "15px",
                        }}
                      >
                        {vendor.name}
                      </Typography>

                      <Chip
                        color="primary"
                        sx={{
                          height: "27px",
                          bgcolor: themeConfig.color,
                          borderRadius: "18px",
                          // color: "black",
                          marginRight: "9px",
                          textTransform: "lowercase",
                          cursor: "unset",
                          width: "max-content",
                          display: "inline-flex",
                          "align-items": " self-start",
                          padding: "2px",
                          "& .MuiChip-label": {
                            fontSize: "15px",
                            fontWeight: "medium",
                            paddingLeft: "9px",
                          },
                        }}
                        icon={
                          <ShoppingBagOutlinedIcon
                            color="inherit"
                            sx={{ paddingBottom: "4px" }}
                          />
                        }
                        label={`${
                          vendor.benefits > 1 || vendor.benefits === 0
                            ? `${vendor.benefits}  parcerias`
                            : `${vendor.benefits}  parceria`
                        } `}
                      />
                      {hasRole("admin", "uppers") && (
                        <Chip
                          color="primary"
                          sx={{
                            height: "27px",
                            bgcolor: themeConfig.color,
                            borderRadius: "18px",
                            // color: "black",
                            textTransform: "lowercase",
                            width: "max-content",
                            display: "inline-flex",
                            "align-items": " self-start",
                            padding: "2px",
                            "& .MuiChip-label": {
                              fontSize: "15px",
                              fontWeight: "medium",
                              paddingLeft: "9px",
                            },
                          }}
                          icon={
                            <PermIdentityIcon
                              sx={{ paddingBottom: "4px" }}
                              color="inherit"
                            />
                          }
                          label={`${
                            vendor.users === 0 || vendor.users > 1
                              ? `${vendor.users}  usuários`
                              : `${vendor.users}  usuário`
                          } `}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {hasRole("admin", "uppers") && (
                        <Typography
                          onClick={() =>
                            navigate(`/${slug}/${vendor.id}/users`, {
                              state: { name: vendor.name, path: vendor.path },
                            })
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {userIcon}
                        </Typography>
                      )}
                      <Typography
                        onClick={() =>
                          navigate(`/${slug}/${vendor.id}/benefits`, {
                            state: { name: vendor.name, path: vendor.path },
                          })
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        {lockIcon}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
      {filteredVendors.length > 5 && (
        <ReactPagination
          page={page}
          rowsPerPage={rowsPerPage}
          count={filteredVendors.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};
